import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import LogoMark from "@/components/LogoMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Selamat datang kembali!");
      navigate("/");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden overflow-hidden bg-brand-espresso lg:block">
        <div className="absolute inset-0 pattern-dots opacity-50" />
        <img
          src="https://images.unsplash.com/photo-1560819400-434c188f63ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Aqiqah"
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <LogoMark size={48} textClass="text-3xl" />
            <div>
              <p className="font-serif text-2xl font-bold text-white">Rizqia</p>
              <p className="text-xs uppercase tracking-[0.3em] text-brand-gold">Aqiqah</p>
            </div>
          </div>
          <div>
            <h1 className="font-serif text-5xl font-bold leading-tight text-white">
              Kelola Pesanan Aqiqah <span className="text-brand-gold">dengan Amanah</span>
            </h1>
            <p className="mt-4 max-w-md text-white/60">
              Sistem manajemen pesanan, paket, dan pelaporan untuk layanan aqiqah Anda —
              rapi, cepat, dan penuh berkah.
            </p>
          </div>
          <p className="text-xs text-white/40">© {new Date().getFullYear()} Rizqia Aqiqah</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center bg-brand-bg px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-3">
              <LogoMark size={44} />
              <p className="font-serif text-2xl font-bold text-brand-espresso">Rizqia Aqiqah</p>
            </div>
          </div>
          <h2 className="font-serif text-3xl font-bold text-brand-espresso">Masuk</h2>
          <p className="mt-1 text-sm text-brand-muted">Silakan masuk untuk melanjutkan.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@email.com"
                required
                className="mt-1.5 focus-visible:ring-brand-gold"
              />
            </div>
            <div>
              <Label htmlFor="password">Kata Sandi</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pr-10 focus-visible:ring-brand-gold"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full bg-brand-gold text-brand-espresso hover:bg-brand-goldhover transition-colors duration-200"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Masuk
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
